import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../services/api";
import Layout from "../components/Layout";
import { Card, CardHeader, CardContent } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { Plus, Search } from "lucide-react";
import EditCollaboratorModal from "../components/collaborators/EditCollaboratorModal";
import { usePermissions } from "../context/PermissionsContext";
import usePagedList from "../hooks/usePagedList";
import PaginationBar from "../components/ui/PaginationBar";
import ExportXlsxButton from "../components/ui/ExportXlsxButton";
import { fmtDateOnly } from "../utils/datetime";

const Collaborators = () => {
  const [collaborators, setCollaborators] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [activeFilter, setActiveFilter] = useState("");
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [selectedCollaborator, setSelectedCollaborator] = useState(null);

  const navigate = useNavigate();
  const { can } = usePermissions();

  const fetchCollaborators = async () => {
    try {
      setLoading(true);

      const params = {};
      if (search) params.search = search;
      if (activeFilter === "true") params.is_active = true;
      if (activeFilter === "false") params.is_active = false;

      const response = await api.get("/collaborators/", { params });
      setCollaborators(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Erro ao buscar colaboradores:", error);
      setCollaborators([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchDepartments = async () => {
    try {
      const response = await api.get("/collaborators/departments");
      setDepartments(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Erro ao buscar departamentos:", error);
      setDepartments([]);
    }
  };

  // Debounce: evita uma requisição por tecla e que respostas fora de ordem
  // sobrescrevam a lista (o clearTimeout cancela a busca anterior pendente).
  useEffect(() => {
    const id = setTimeout(fetchCollaborators, 300);
    return () => clearTimeout(id);
  }, [search, activeFilter]);

  useEffect(() => {
    fetchDepartments();
  }, []);

  const openEdit = (collaborator, e) => {
    if (e) e.stopPropagation();
    setSelectedCollaborator(collaborator);
    setIsEditOpen(true);
  };

  const closeEdit = () => {
    setSelectedCollaborator(null);
    setIsEditOpen(false);
  };

  const handleEditSubmit = async (formData) => {
    if (!selectedCollaborator?.id) return;

    try {
      await api.put(`/collaborators/${selectedCollaborator.id}`, {
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email || null,
        birth_date: formData.birth_date || null,
        hire_date: formData.hire_date || null,
        job_title: formData.job_title || null,
        department_id:
          formData.department_id && /^\d+$/.test(String(formData.department_id))
            ? Number(formData.department_id)
            : null,
        department: formData.department || null,
        cost_center: formData.cost_center || null,
        manager_id: formData.manager_id ? Number(formData.manager_id) : null,
        country: formData.country || null,
        is_active: formData.is_active,
      });

      closeEdit();
      await fetchCollaborators();
    } catch (error) {
      console.error("Erro ao atualizar colaborador:", error.response?.data || error);
      alert("Erro ao atualizar colaborador.");
    }
  };

  const handleDelete = async (id) => {
    try {
      closeEdit();
      await api.delete(`/collaborators/${id}`);
      await fetchCollaborators();
    } catch (error) {
      console.error("Erro ao deletar colaborador:", error.response?.data || error);
      alert("Erro ao deletar colaborador.");
    }
  };

  const getStatusBadgeClass = (isActive) =>
    isActive ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-800";

  const formatDate = (value) => fmtDateOnly(value) || "—";

  const displayName = (c) =>
    `${c.first_name || ""} ${c.last_name || ""}`.trim() || "—";

  const rows = useMemo(() => collaborators || [], [collaborators]);
  const pg = usePagedList(rows);

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Colaboradores</h1>
            <p className="text-gray-500">Cadastre colaboradores</p>
          </div>

          <div className="flex flex-wrap gap-2">
            <ExportXlsxButton url="/collaborators/export" filename="colaboradores.xlsx" />
            <Button variant="outline" onClick={() => navigate("/collaborators/fluxograma")}>
              Ver Fluxograma
            </Button>
            <Button variant="outline" onClick={() => navigate("/collaborators/departments")}>
              Departamentos
            </Button>
            {can("create", "/collaborators") && (
              <Button onClick={() => navigate("/collaborators/new")}>
                <Plus className="w-4 h-4 mr-2" />
                Novo Colaborador
              </Button>
            )}
          </div>
        </div>

        <Card>
          <CardHeader>
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <Input
                    placeholder="Buscar por nome, e-mail, depto, cargo..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>

              <div className="flex gap-2">
                <select
                  value={activeFilter}
                  onChange={(e) => setActiveFilter(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm"
                >
                  <option value="">Todos</option>
                  <option value="true">Ativos</option>
                  <option value="false">Inativos</option>
                </select>
              </div>
            </div>
          </CardHeader>

          <CardContent>
            {loading ? (
              <div className="text-center py-8 text-gray-500">Carregando...</div>
            ) : rows.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                Nenhum colaborador encontrado
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[1080px]">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Nome</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">E-mail</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Departamento</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Centro de custo</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Cargo</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Gerente</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Admissão</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Status</th>
                      <th className="text-right py-3 px-4 text-sm font-semibold text-gray-600">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pg.pageItems.map((c) => (
                      <tr
                        key={c.id}
                        onClick={() => navigate(`/collaborators/${c.id}`)}
                        className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer"
                      >
                        <td className="py-3 px-4 text-sm font-medium text-gray-900">{displayName(c)}</td>
                        <td className="py-3 px-4 text-sm text-gray-600">{c.email || "—"}</td>
                        <td className="py-3 px-4 text-sm text-gray-600">{c.department || "—"}</td>
                        <td className="py-3 px-4 text-sm text-gray-600">{c.cost_center || "—"}</td>
                        <td className="py-3 px-4 text-sm text-gray-600">{c.job_title || "—"}</td>
                        <td className="py-3 px-4 text-sm text-gray-600">{c.manager_name || "—"}</td>
                        <td className="py-3 px-4 text-sm text-gray-600">{formatDate(c.hire_date)}</td>
                        <td className="py-3 px-4">
                          <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusBadgeClass(c.is_active)}`}>
                            {c.is_active ? "Ativo" : "Inativo"}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-right text-sm">
                          <Button
                            variant="outline"
                            onClick={(e) => openEdit(c, e)}
                            className="h-auto px-3 py-1 text-xs text-gray-700"
                          >
                            Editar
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <PaginationBar
                  page={pg.page}
                  pageSize={pg.pageSize}
                  total={pg.total}
                  onPageChange={pg.onPageChange}
                  onPageSizeChange={pg.onPageSizeChange}
                />
              </div>
            )}
          </CardContent>
        </Card>

        <EditCollaboratorModal
          isOpen={isEditOpen}
          onClose={closeEdit}
          collaborator={selectedCollaborator}
          departments={departments}
          managers={collaborators}
          onSubmit={handleEditSubmit}
          onDelete={handleDelete}
        />
      </div>
    </Layout>
  );
};

export default Collaborators;
